<template>
  <div class="lp-attr">
    <div v-if="busy" class="lp-attr-loading"><CygnusIcon name="spinner" :spin="true" class="me-1" />Loading attribution…</div>
    <div v-else-if="error" class="lp-attr-alert">{{ error }}</div>
    <template v-else-if="data">

      <div class="lp-attr-grid">
        <!-- LEFT: funnel (absolute counts) + rate (the one bar) -->
        <div class="lp-panel">
          <div class="lp-panel-h">Conversion <span class="lp-panel-rp">since published</span></div>

          <div class="lp-fn-cap">This page → your pipeline</div>
          <div class="lp-funnel">
            <div class="lp-fn">
              <div class="lp-fn-v">{{ data.totals.views }}</div>
              <div class="lp-fn-k">Page views</div>
              <div class="lp-fn-bar s1"></div>
            </div>
            <span class="lp-fn-arw">→</span>
            <div class="lp-fn">
              <div class="lp-fn-v">{{ data.totals.submissions }}</div>
              <div class="lp-fn-k">Form submissions</div>
              <div class="lp-fn-bar s2"></div>
            </div>
            <span class="lp-fn-arw">→</span>
            <div class="lp-fn">
              <div class="lp-fn-v">{{ data.totals.enrolled }}</div>
              <div class="lp-fn-k">Enrolled in flow</div>
              <div class="lp-fn-bar s3"></div>
            </div>
          </div>

          <!-- the OTHER idiom: rate as a bar, in its own tinted block -->
          <div class="lp-rate">
            <div class="lp-rate-v">{{ data.totals.conversion }}%</div>
            <div class="lp-rate-r">
              <div class="lp-rate-k"><span>View → submission rate</span><span>{{ data.totals.submissions }} of {{ data.totals.views }}</span></div>
              <div class="lp-rate-bar"><span :style="{ width: barWidth(data.totals.conversion) }"></span></div>
            </div>
          </div>

          <p v-if="awaiting > 0" class="lp-attr-note">
            <CygnusIcon name="info-circle" class="me-1" />
            <span><b>{{ awaiting }} submission{{ awaiting === 1 ? '' : 's' }}</b> not yet enrolled — awaiting email confirmation (double opt-in). They enrol automatically once confirmed.</span>
          </p>
        </div>

        <!-- RIGHT: where views came from -->
        <div class="lp-panel">
          <div class="lp-panel-h">Where views came from</div>
          <div v-if="data.utm_sources.length" class="lp-src-chips">
            <span v-for="s in data.utm_sources" :key="s.source" class="lp-src">
              <span class="lp-src-utm">{{ s.source }}</span>
              <span class="lp-src-n">{{ s.views }}</span>
            </span>
          </div>
          <p v-else class="lp-attr-note">No views recorded yet.</p>
          <p class="lp-hint mt-2">Tagged from each visitor’s <code>utm_source</code> — add UTMs to your links to split traffic further.</p>
        </div>
      </div>

      <!-- per-pipeline funnels -->
      <div v-if="data.pipelines.length" class="lp-panel mt-3">
        <div class="lp-panel-h">Per-pipeline breakdown</div>
        <div v-for="p in data.pipelines" :key="p.flow_id" class="lp-pipe">
          <div class="lp-pipe-h">
            <span class="lp-pipe-name">{{ p.name }}</span>
            <span v-if="p.audience_descriptor" class="lp-pipe-aud">{{ p.audience_descriptor }}</span>
          </div>
          <div class="lp-pipe-counts">
            <span><b>{{ p.submissions }}</b> submissions</span>
            <span class="lp-fn-arw">→</span>
            <span><b>{{ p.confirmed }}</b> confirmed</span>
            <span class="lp-fn-arw">→</span>
            <span><b>{{ p.enrolled }}</b> enrolled</span>
          </div>
        </div>

        <!-- unattributed bucket — the honest catch-all -->
        <div v-if="data.unattributed.submissions > 0" class="lp-pipe lp-pipe--unattr">
          <div class="lp-pipe-h"><span class="lp-pipe-name">Unattributed</span></div>
          <div class="lp-pipe-counts">
            <span><b>{{ data.unattributed.submissions }}</b> submissions</span>
            <span class="lp-fn-arw">→</span>
            <span><b>{{ data.unattributed.confirmed }}</b> confirmed</span>
            <span class="lp-pipe-note">— no UTM / referrer / default matched these</span>
          </div>
        </div>
      </div>

      <!-- wiring summary -->
      <div class="lp-wiring mt-3">
        <p><b>Where submissions go.</b> {{ wiringSentence }}</p>
      </div>
    </template>
  </div>
</template>

<script>
import useApi from "@apexl/icarus-core/src/services/api";

// Landing pages P1b — the per-pipeline attribution panel (Screen C). Renders
// GET /landing-pages/{id}/attribution: the funnel is ABSOLUTE counts, the
// conversion is the one rate (a bar), utm_source chips, the honest unattributed
// bucket, and a plain-English wiring sentence. Nothing is denormalised — the
// backend derives it all at read time from the visit log + submissions.
export default {
  name: "LandingPageAttribution",
  props: {
    pageId: { type: Number, required: true },
    apiBase: { type: String, default: "/api/v1" },
    reloadKey: { type: Number, default: 0 },
  },
  data() {
    return { api: useApi(), data: null, busy: false, error: null };
  },
  computed: {
    awaiting() {
      if (!this.data) return 0;
      return Math.max(0, this.data.totals.submissions - this.data.totals.enrolled);
    },
    wiringSentence() {
      if (!this.data) return "";
      const ps = this.data.pipelines.filter((p) => p.submissions > 0 || p.enrolled > 0);
      if (!ps.length && !this.data.pipelines.length) {
        return "No pipeline is linked yet — submissions are captured but not auto-enrolled. Link one under the builder’s Pipelines section.";
      }
      const names = (ps.length ? ps : this.data.pipelines).map((p) => p.name);
      const list = names.length === 1 ? names[0] : names.slice(0, -1).join(", ") + " and " + names[names.length - 1];
      return `Every matched submission auto-enrols into ${list} — the drip flow still respects the runner-tick send gate, so publishing a page never bypasses sending controls.`;
    },
  },
  watch: {
    reloadKey() { this.load(); },
  },
  mounted() { this.load(); },
  methods: {
    barWidth(pct) {
      const n = Math.max(0, Math.min(100, Number(pct) || 0));
      return `${n}%`;
    },
    async load() {
      this.busy = true; this.error = null;
      try {
        const res = await this.api.get(`${this.apiBase}/admin/landing-pages/${this.pageId}/attribution`);
        const payload = res?.data ?? res;
        // normalise the shapes so the template never trips on a missing key.
        this.data = {
          totals: {
            views: payload?.totals?.views || 0,
            submissions: payload?.totals?.submissions || 0,
            confirmed: payload?.totals?.confirmed || 0,
            enrolled: payload?.totals?.enrolled || 0,
            conversion: payload?.totals?.conversion || 0,
          },
          pipelines: payload?.pipelines || [],
          unattributed: { submissions: payload?.unattributed?.submissions || 0, confirmed: payload?.unattributed?.confirmed || 0 },
          utm_sources: payload?.utm_sources || [],
        };
      } catch (e) {
        this.error = e?.response?.data?.error || e?.message || "Failed to load attribution.";
      }
      this.busy = false;
    },
  },
};
</script>

<style scoped>
.lp-attr-loading, .lp-attr-alert { font-size: .84rem; color: var(--m-text-secondary); padding: 16px; }
.lp-attr-alert { color: var(--m-status-danger); }
.lp-attr-grid { display: grid; grid-template-columns: 1.5fr 1fr; gap: 16px; align-items: start; }
.lp-panel { background: var(--m-bg-card, #fff); border: 1px solid var(--m-border); border-radius: 14px; box-shadow: var(--m-shadow-card); padding: 16px; }
.lp-panel-h { font-size: .66rem; font-weight: 800; letter-spacing: .09em; text-transform: uppercase; color: var(--m-text-tertiary); display: flex; align-items: center; margin-bottom: 14px; }
.lp-panel-rp { margin-left: auto; font-size: .68rem; font-weight: 500; letter-spacing: 0; text-transform: none; }

.lp-fn-cap { font-size: .62rem; font-weight: 700; letter-spacing: .06em; text-transform: uppercase; color: var(--m-text-tertiary); margin-bottom: 6px; }
.lp-funnel { display: flex; align-items: stretch; background: var(--m-bg-content); border: 1px solid var(--m-border); border-radius: 10px; overflow: hidden; }
.lp-fn { flex: 1; padding: 14px 8px 12px; text-align: center; }
.lp-fn-v { font-size: 1.5rem; font-weight: 800; letter-spacing: -.02em; line-height: 1; }
.lp-fn-k { font-size: .6rem; font-weight: 700; letter-spacing: .04em; text-transform: uppercase; color: var(--m-text-tertiary); margin-top: 5px; }
.lp-fn-bar { height: 3px; border-radius: 999px; margin: 9px auto 0; width: 60%; }
.lp-fn-bar.s1 { background: var(--m-accent); }
.lp-fn-bar.s2 { background: #6366F1; width: 45%; }
.lp-fn-bar.s3 { background: var(--m-accent-2); width: 35%; }
.lp-fn-arw { align-self: center; color: var(--m-text-tertiary); font-size: .8rem; padding: 0 2px; }

.lp-rate { margin-top: 16px; display: flex; align-items: center; gap: 14px; background: #E9FBF8; border: 1px solid #B8EDE5; border-radius: 10px; padding: 12px 15px; }
.lp-rate-v { font-size: 1.35rem; font-weight: 800; color: #0F766E; min-width: 58px; }
.lp-rate-r { flex: 1; }
.lp-rate-k { font-size: .72rem; font-weight: 700; color: var(--m-text-secondary); display: flex; justify-content: space-between; margin-bottom: 6px; }
.lp-rate-k span:last-child { color: var(--m-text-tertiary); font-weight: 500; }
.lp-rate-bar { height: 8px; border-radius: 999px; background: #fff; overflow: hidden; border: 1px solid #B8EDE5; }
.lp-rate-bar span { display: block; height: 100%; border-radius: 999px; background: var(--m-accent-2); }

.lp-attr-note { font-size: .73rem; color: var(--m-text-tertiary); margin-top: 11px; display: flex; align-items: flex-start; gap: 6px; }
.lp-attr-note b { color: var(--m-text-secondary); }

.lp-src-chips { display: flex; flex-wrap: wrap; gap: 8px; }
.lp-src { display: inline-flex; align-items: center; gap: 7px; font-size: .76rem; background: var(--m-bg-content); border: 1px solid var(--m-border); border-radius: 8px; padding: 6px 10px; }
.lp-src-utm { font-family: var(--m-font-mono); font-size: .7rem; color: var(--m-status-info); background: #E6F4FC; border-radius: 4px; padding: 1px 5px; font-weight: 700; }
.lp-src-n { color: var(--m-text-primary); font-weight: 800; }
.lp-hint { font-size: .72rem; color: var(--m-text-tertiary); }
.lp-hint code { font-family: var(--m-font-mono); background: var(--m-bg-content); border: 1px solid var(--m-border); border-radius: 4px; padding: 0 4px; }
.mt-2 { margin-top: 8px; } .mt-3 { margin-top: 16px; }

.lp-pipe { border: 1px solid var(--m-border); border-radius: 10px; padding: 10px 12px; margin-bottom: 8px; }
.lp-pipe--unattr { background: var(--m-bg-content); border-style: dashed; }
.lp-pipe-h { display: flex; align-items: baseline; gap: 10px; margin-bottom: 6px; }
.lp-pipe-name { font-size: .82rem; font-weight: 700; }
.lp-pipe-aud { font-size: .72rem; color: var(--m-text-tertiary); }
.lp-pipe-counts { display: flex; align-items: center; gap: 10px; font-size: .8rem; color: var(--m-text-secondary); flex-wrap: wrap; }
.lp-pipe-counts b { color: var(--m-text-primary); font-weight: 800; }
.lp-pipe-note { color: var(--m-text-tertiary); font-size: .72rem; }

.lp-wiring { background: var(--m-accent-soft); border: 1px solid #DDE1FF; border-left: 3px solid var(--m-accent); border-radius: 10px; padding: 13px 15px; }
.lp-wiring p { font-size: .84rem; color: var(--m-text-primary); line-height: 1.55; margin: 0; }

@media (max-width: 900px) { .lp-attr-grid { grid-template-columns: 1fr; } }
</style>
